import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import APFormGroup from 'components/FormFields/APFormGroup';
import ViewOptions, { ViewOptionsSelect, ViewOptionsHeading, ViewOptionsCheckbox } from 'components/ViewOptions';

/*
const calendarViewOptions = [
  { label: 'Calendar', value: 'dayGridMonth' },
  { label: 'Day', value: 'timeGridDay' },
  { label: 'Week', value: 'timeGridWeek' },
  { label: 'Week List', value: 'listWeek' },
  { label: 'Day List', value: 'listDay' },
  { label: 'Year List', value: 'listYear' },
];
*/

const DAYS = [
  { value: 'sunday', label: 'Sunday', day: 0 },
  { value: 'monday', label: 'Monday', day: 1 },
  { value: 'tuesday', label: 'Tuesday', day: 2 },
  { value: 'wednesday', label: 'Wednesday', day: 3 },
  { value: 'thursday', label: 'Thursday', day: 4 },
  { value: 'friday', label: 'Friday', day: 5 },
  { value: 'saturday', label: 'Saturday', day: 6 },
];

const VisibleItem = ({ name, projectName, projectDate, onChange, options }) => {
  let option = '';
  if (options !== undefined) {
    option = options[name];
  }

  const inputProps = {
    onChange,
    checked: option,
  };

  return (
    <ViewOptionsCheckbox id={`${name}`}
      inputProps={inputProps}
    >
      <span className="project-name">{projectName}</span>
      <span className="project-date">| {projectDate}</span>
    </ViewOptionsCheckbox>
  );
};

const CalendarViewOptions = ({
  currentProjects,
  options,
  calendarMicrofilters,
  handleProjectExclusionToggle,
  handleAllProjectExclusions,
  handleMicrofilterChange,
  handleFirstDayChange,
}) => {
  if (!options) return;

  const { firstDayOfWeek, weddingExclusions } = options || {};
  const { events, tasks, payments } = calendarMicrofilters || {};
  const projects = currentProjects;
  const firstDay = DAYS.find(day => day.day === firstDayOfWeek) || DAYS[0];
  const projectsOptions = useMemo(() => {
    return currentProjects.reduce((obj, project) => {
      const stringId = String(project.id);
      obj[stringId] = !weddingExclusions.includes(stringId);
      return obj;
    }, {});
  }, [weddingExclusions, currentProjects]);

  const firstDayOnChange = (e) => {
    const { value } = e.target;
    const firstDayObject = DAYS.find(dayOfWeek => dayOfWeek.value === value);

    if (firstDayObject) {
      handleFirstDayChange(firstDayObject.day);
    }
  };

  const onChangeMicrofilter = (type, kind, value) => {
    handleMicrofilterChange(type, kind, value);
  };

  return (
    <>
      <ViewOptions
        adjustOnVerticalOverflow={false}
        scrollLockOnOpen={false}
        menuClass="view-options__menu--calendar"
      >
        <div className="view-options__section view-options__section--calendar-dropdowns">
          <APFormGroup
            verticalGap={0}
            gridGap={1}
          >
            <div className="view-options__form-item">
              <ViewOptionsHeading>Calendar View</ViewOptionsHeading>
              <ViewOptionsSelect
                inputProps={{
                  onChange: () => {},
                  value: 'month',
                  disabled: true,
                }}
              >
                <option
                  value="month"
                >
                  Month
                </option>
                <option
                  value="null"
                >
                  Null
                </option>
              </ViewOptionsSelect>
            </div>
            <div className="view-options__form-item view-options__form-item--first-day">
              <ViewOptionsHeading>First Day of Week</ViewOptionsHeading>
              <ViewOptionsSelect
                inputProps={{
                  onChange: firstDayOnChange,
                  value: firstDay.value,
                }}
              >
                {DAYS.map((option, index) => (
                  <option value={option.value}
                    key={`option_${index}`}
                  >
                    {option.label}
                  </option>
                ))}
              </ViewOptionsSelect>
            </div>
          </APFormGroup>
        </div>
        <div className="view-options__section">
          <APFormGroup
            verticalGap={0}
            gridGap={1}
          >
            <div className="view-options__form-section">
              <ViewOptionsHeading>Calendar Events</ViewOptionsHeading>
              {/* Not releasing yet.
              <ViewOptionsCheckbox
                id="microfilter-event-general"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('events', 'general', e.target.checked),
                  checked: events.general,
                }}
              >
                General calendar events
              </ViewOptionsCheckbox>
              */}
              <ViewOptionsCheckbox
                id="microfilter-event-project"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('events', 'project', e.target.checked),
                  checked: events.project,
                }}
              >
                Project-related calendar events
              </ViewOptionsCheckbox>
              <ViewOptionsHeading>Tasks</ViewOptionsHeading>
              <ViewOptionsCheckbox
                id="microfilter-task-general"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('tasks', 'general', e.target.checked),
                  checked: tasks.general,
                }}
              >
                General tasks
              </ViewOptionsCheckbox>
              <ViewOptionsCheckbox
                id="microfilter-task-sales"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('tasks', 'sales', e.target.checked),
                  checked: tasks.sales,
                }}
              >
                Sales-related tasks
              </ViewOptionsCheckbox>
              <ViewOptionsCheckbox
                id="microfilter-task-project"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('tasks', 'project', e.target.checked),
                  checked: tasks.project,
                }}
              >
                Project-related tasks
              </ViewOptionsCheckbox>
              <ViewOptionsCheckbox
                id="microfilter-task-assigned"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('tasks', 'assigned', e.target.checked),
                  checked: tasks.assigned,
                }}
              >
                Only tasks assigned to me
              </ViewOptionsCheckbox>
              <ViewOptionsCheckbox
                id="microfilter-task-completed"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('tasks', 'completed', e.target.checked),
                  checked: tasks.completed,
                }}
              >
                Completed tasks
              </ViewOptionsCheckbox>
              <ViewOptionsHeading>Payments</ViewOptionsHeading>
              <ViewOptionsCheckbox
                id="microfilter-payments-invoice"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('payments', 'invoice', e.target.checked),
                  checked: payments.invoice,
                }}
              >
                AP Invoice related payments
              </ViewOptionsCheckbox>
              <ViewOptionsCheckbox
                id="microfilter-payments-vendor"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('payments', 'vendor', e.target.checked),
                  checked: payments.vendor,
                }}
              >
                Project-related vendor payments
              </ViewOptionsCheckbox>
              <ViewOptionsCheckbox
                id="microfilter-payments-completed"
                inputProps={{
                  onChange: (e) => onChangeMicrofilter('payments', 'completed', e.target.checked),
                  checked: payments.completed,
                }}
              >
                Completed payments
              </ViewOptionsCheckbox>
            </div>
            <div className="view-options__form-section view-options__form-section--projects">
              <div className="view-options__section--projects-list">
                <ViewOptionsHeading>Booked Projects</ViewOptionsHeading>
                {projects.map((item) => {
                  const formattedProjectDate = dayjs(item.weddingDate).format('MMM D, YYYY');
                  return (
                    <VisibleItem
                      key={item.id}
                      name={item.id}
                      projectName={item.name}
                      projectDate={formattedProjectDate}
                      options={projectsOptions}
                      onChange={handleProjectExclusionToggle}
                    />
                  );
                })}
              </div>
              <div className="button-group">
                <button
                  className="button--unstyled gold--large"
                  onClick={() => handleAllProjectExclusions(false)}
                >
                  Select All
                </button>
                <button
                  className="button--unstyled gold--large"
                  onClick={() => handleAllProjectExclusions(true)}
                >
                  Deselect All
                </button>
              </div>
            </div>

          </APFormGroup>
        </div>

      </ViewOptions>
    </>
  );
};

export default CalendarViewOptions;
